import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { cn } from "../lib/utils";
import TeamUtilizationSection from "../components/TeamUtilizationSection";
import { statsApi, salesServicesApi, crmApi } from "../lib/api";
import { useCurrency } from "../context/CurrencyContext";
import {
  TrendingUp,
  TrendingDown,
  ArrowRight,
  Activity,
  AlertTriangle,
  Building2,
  Users,
  Target,
  Clock,
  ChevronLeft,
  ChevronRight,
  Trophy,
  Flame,
  FileQuestion,
  Send,
} from "lucide-react";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Line,
  ComposedChart,
  BarChart,
  Bar,
  Legend,
} from "recharts";

const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const fmt = (n) => `$${Math.round(n || 0).toLocaleString("en-US")}`;
const fmtK = (n) => {
  const v = Math.round(n || 0);
  if (Math.abs(v) >= 1000) return `$${Math.round(v / 1000)}k`;
  return `$${v}`;
};

// Month total printed above a stacked bar: rendered by a zero-height bar at the
// top of the stack, so `y` is already the top edge of the column.
const stackTotalLabel = (rows, getTotal) => ({ x, y, width, index }) => {
  const total = rows?.[index] ? getTotal(rows[index]) : 0;
  if (!total) return null;
  return (
    <text x={x + width / 2} y={y - 6} textAnchor="middle" fill="#a1a1aa" fontSize={11} fontFamily="monospace">
      {fmtK(total)}
    </text>
  );
};

const DashboardV2Page = () => {
  const navigate = useNavigate();
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [chartView, setChartView] = useState("fact"); // plan | fact
  // Cash-flow history length; the 3 forecast months are always added on top.
  const [historyMonths, setHistoryMonths] = useState(6);
  const [byManager, setByManager] = useState(false);
  const [salesServices, setSalesServices] = useState([]);
  const [crmPayments, setCrmPayments] = useState([]);

  const periodKey = useMemo(() => `${year}-${String(month).padStart(2, "0")}`, [year, month]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const [overviewRes, salesRes, crmRes] = await Promise.all([
          statsApi.getDashboardV2Overview(periodKey, historyMonths),
          salesServicesApi.getAll().catch(() => ({ data: [] })),
          crmApi.getPayments({}).catch(() => ({ data: [] })),
        ]);
        if (cancelled) return;
        setData(overviewRes.data);
        setSalesServices(salesRes.data || []);
        setCrmPayments(crmRes.data || []);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || e.message || "Failed to load");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [periodKey, historyMonths]);

  // Currency context (for client-side toUSD when computing by-manager)
  const { rates: currencyRates } = useCurrency();
  const usdToUah = currencyRates?.USD || 42.97;
  const eurToUah = currencyRates?.EUR || 50.91;

  // Last 6 months ending with selected — for the income/expense breakdown charts.
  // Pulled from data.cash_flow which already has the month_key + plan/fact totals.
  const last6Months = useMemo(() => {
    const out = [];
    for (let offset = 5; offset >= 0; offset--) {
      let m = month - offset;
      let y = year;
      while (m <= 0) {
        m += 12;
        y -= 1;
      }
      out.push({ y, m, key: `${y}-${String(m).padStart(2, "0")}`, label: MONTH_NAMES[m - 1] });
    }
    return out;
  }, [year, month]);

  const toUSD = (amount, currency) => {
    if (!amount || isNaN(amount)) return 0;
    const cur = (currency || "UAH").toUpperCase();
    if (cur === "USD") return Number(amount);
    if (cur === "EUR") return Number(amount) * (eurToUah / usdToUah);
    return Number(amount) / usdToUah;
  };

  // Per-manager breakdown for income chart (when byManager toggle is on).
  // Same convention as /general-pnl and income_fact_month() on the backend:
  // plan by the booked month, fact by the month the money actually arrived.
  const factMonthKey = (rec) => {
    const pd = rec.paid_date || rec.payment_date;
    if (pd) {
      const iso = String(pd).match(/^(\d{4})-(\d{2})/);
      if (iso) return `${iso[1]}-${iso[2]}`;
      const legacy = String(pd).match(/^(\d{2})\.(\d{2})\.(\d{4})/);
      if (legacy) return `${legacy[3]}-${legacy[2]}`;
    }
    return rec.month || null;
  };

  const incomeByManager = useMemo(() => {
    const managers = new Set();
    (salesServices || []).forEach((s) => {
      managers.add(s.sales_manager_name || "Unassigned");
    });
    const managerList = Array.from(managers).sort();
    const rows = last6Months.map(({ key, label }) => {
      const row = { month: label, month_key: key };
      managerList.forEach((m) => {
        row[m] = 0;
      });
      row["CRM (recurring)"] = 0;
      (salesServices || []).forEach((s) => {
        if (chartView === "fact") {
          if (!s.is_paid || factMonthKey(s) !== key) return;
        } else if (s.month !== key) return;
        const mgr = s.sales_manager_name || "Unassigned";
        row[mgr] += toUSD(s.amount, s.currency);
      });
      (crmPayments || []).forEach((c) => {
        if (chartView === "fact") {
          if (c.status !== "paid" || factMonthKey(c) !== key) return;
        } else if (c.month !== key) return;
        const amt = chartView === "fact"
          ? (c.actual_amount || c.planned_amount || 0)
          : (c.planned_amount || 0);
        row["CRM (recurring)"] += toUSD(amt, c.currency);
      });
      managerList.forEach((m) => {
        row[m] = Math.round(row[m]);
      });
      row["CRM (recurring)"] = Math.round(row["CRM (recurring)"]);
      return row;
    });
    return { rows, managers: managerList };
  }, [salesServices, crmPayments, last6Months, chartView, usdToUah, eurToUah]);

  const MANAGER_COLORS = [
    "#10b981", "#06b6d4", "#3b82f6", "#a78bfa", "#ec4899",
    "#f97316", "#eab308", "#84cc16", "#14b8a6", "#f43f5e",
  ];

  const handlePrev = () => {
    if (month === 1) {
      setMonth(12);
      setYear(year - 1);
    } else {
      setMonth(month - 1);
    }
  };
  const handleNext = () => {
    if (month === 12) {
      setMonth(1);
      setYear(year + 1);
    } else {
      setMonth(month + 1);
    }
  };

  const periodLabel = `${MONTH_NAMES[month - 1]} ${year}`;

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-orange-500" />
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="rounded-lg border border-red-500/30 bg-red-500/5 p-6">
        <div className="flex items-center gap-2 text-red-400">
          <AlertTriangle className="h-4 w-4" />
          <span className="font-semibold">Failed to load dashboard</span>
        </div>
        <p className="text-sm text-red-300/70 mt-2 font-mono">{error}</p>
      </div>
    );
  }

  const k = data?.kpi || {};
  const cashFlow = data?.cash_flow || [];
  // The backend appends 3 forecast months (is_future, income only); the
  // category bar charts below stay on the 6 real months.
  const pastFlow = cashFlow.filter((r) => !r.is_future);
  const hasForecast = cashFlow.some((r) => r.is_future);
  const forecastMethod = cashFlow.find((r) => r.is_future)?.expenses_forecast_method;
  // Category bars: the 6 real months + 3 forecast months. Forecast amounts
  // sit in their own *_fc keys and draw as faded bars on the same stack. The
  // current month is not closed: on top of what is already there it gets the
  // still-expected remainder per category (salaries, for one, are paid at the
  // start of next month, so a live month alone reads as "no payroll").
  const view = chartView === "plan" ? "plan" : "fact";
  const isCurrent = (r) => !r.is_future && r.expenses_forecast != null;
  const rest = (expected, have) => Math.max(0, (expected || 0) - (have || 0));
  const EXP_CATS = ["salary", "artist", "ad_fee", "other", "ads"];
  const INC_CATS = ["sales", "partner", "crm", "support"];
  const expenseFlow = [
    ...pastFlow.map((r) => {
      if (!isCurrent(r)) return r;
      const b = r.expenses_forecast_breakdown || {};
      const fc = Object.fromEntries(EXP_CATS.map((c) => [`${c}_fc`, rest(b[c], r[`${c}_${view}`])]));
      const total = (r[`expenses_${view}`] || 0) + Object.values(fc).reduce((a, v) => a + v, 0);
      return { ...r, ...fc, label_total: total };
    }),
    ...cashFlow.filter((r) => r.is_future).map((r) => {
      const b = r.expenses_forecast_breakdown || {};
      return {
        month: r.month, is_future: true, label_total: r.expenses_forecast,
        ...Object.fromEntries(EXP_CATS.map((c) => [`${c}_fc`, b[c]])),
      };
    }),
  ];
  const incomeFlow = [
    ...pastFlow.map((r) => {
      if (!isCurrent(r) || view === "plan") return r;
      const fc = Object.fromEntries(INC_CATS.map((c) => [`${c}_fc`, rest(r[`${c}_plan`], r[`${c}_fact`])]));
      const total = (r.income_fact || 0) + Object.values(fc).reduce((a, v) => a + v, 0);
      return { ...r, ...fc, label_total: total };
    }),
    ...cashFlow.filter((r) => r.is_future).map((r) => ({
      month: r.month, is_future: true, label_total: r.income_forecast,
      ...Object.fromEntries(INC_CATS.map((c) => [`${c}_fc`, r[`${c}_plan`]])),
    })),
  ];
  const pipeline = data?.pipeline || [];
  const closing = data?.closing_this_month || [];
  const arAging = data?.ar_aging || [];
  const topClients = data?.top_clients_ytd || [];
  const profitability = data?.project_profitability || [];
  const worstOffender = data?.ar_worst_offender;

  const arOverdueShare = k.ar_outstanding ? (k.ar_overdue / k.ar_outstanding) * 100 : 0;

  const pipelineTotalWeighted = pipeline.reduce((s, p) => s + (p.weighted || 0), 0);
  const pipelineTotalValue = pipeline.reduce((s, p) => s + (p.value || 0), 0);
  const pipelineTotalDeals = pipeline.reduce((s, p) => s + (p.deals || 0), 0);
  const closingTotal = closing.reduce((s, d) => s + (d.value || 0), 0);
  const closingWeighted = closing.reduce((s, d) => s + ((d.value || 0) * (d.prob || 0)) / 100, 0);

  // 90+ days is written off (BAD_DEBT_DAYS on the backend) — not counted as money owed.
  const arTotal = arAging.reduce((s, b) => s + (b.written_off ? 0 : b.amount || 0), 0);
  const arWrittenOff = arAging.reduce((s, b) => s + (b.written_off ? b.amount || 0 : 0), 0);
  const arScale = arTotal + arWrittenOff;

  const top3Concentration = data?.top_clients_concentration_pct || 0;

  const stageColor = (idx) => {
    const colors = ["bg-yellow-500", "bg-blue-500", "bg-emerald-500", "bg-orange-500", "bg-violet-500", "bg-cyan-500"];
    return colors[idx % colors.length];
  };

  const arBucketColor = (idx) => ["bg-emerald-500", "bg-yellow-500", "bg-orange-500", "bg-red-500"][idx] || "bg-zinc-500";

  return (
    <div data-testid="dashboard-v2" className="space-y-6">
      {/* Header */}
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-2xl font-bold text-zinc-50 font-['Manrope']">Owner overview</h1>
          <p className="text-sm text-zinc-500">Cash, pipeline, margin — period · {periodLabel}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={handlePrev} className="border-zinc-800 bg-zinc-900 hover:bg-zinc-800 h-9 w-9">
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <div className="px-3 py-1.5 bg-zinc-900 border border-zinc-800 rounded-md text-sm text-zinc-50 font-medium min-w-[110px] text-center">
            {periodLabel}
          </div>
          <Button variant="outline" size="icon" onClick={handleNext} className="border-zinc-800 bg-zinc-900 hover:bg-zinc-800 h-9 w-9">
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* === ROW 1.5: Hard cashflow KPIs (link to /cashflow drill-down) === */}
      {data?.cashflow_summary && (
        <Link to="/cashflow" className="block">
          <div className="rounded-md border border-zinc-800 bg-zinc-950/50 p-3 hover:border-orange-500/40 hover:bg-zinc-950/80 transition-colors">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-2">
                <Flame className="h-4 w-4 text-orange-500" />
                <span className="text-xs uppercase font-mono tracking-wide text-zinc-500">Hard cashflow</span>
                <span className="text-xs text-zinc-600">based on invoice send dates</span>
              </div>
              <div className="flex items-center gap-5 text-xs">
                <div className="flex items-center gap-1.5">
                  <span className="inline-block w-2 h-2 rounded-full bg-red-500" />
                  <span className="text-zinc-400">Overdue</span>
                  <span className="font-mono text-zinc-50 font-semibold">{fmt(data.cashflow_summary.overdue_total)}</span>
                  <span className="text-zinc-600">·{data.cashflow_summary.overdue_count}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Clock className="h-3 w-3 text-amber-400" />
                  <span className="text-zinc-400">Pending</span>
                  <span className="font-mono text-zinc-50 font-semibold">{fmt(data.cashflow_summary.pending_total)}</span>
                  <span className="text-zinc-600">·{data.cashflow_summary.pending_count}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <FileQuestion className="h-3 w-3 text-sky-400" />
                  <span className="text-zinc-400">Not invoiced</span>
                  <span className="font-mono text-zinc-50 font-semibold">{fmt(data.cashflow_summary.not_invoiced_total)}</span>
                  <span className="text-zinc-600">·{data.cashflow_summary.not_invoiced_count}</span>
                </div>
                {data.cashflow_summary.written_off_count > 0 && (
                  <div className="flex items-center gap-1.5" data-testid="hard-cashflow-written-off">
                    <span className="inline-block w-2 h-2 rounded-full bg-zinc-600" />
                    <span className="text-zinc-400">Written off 90+</span>
                    <span className="font-mono text-red-400 font-semibold">−{fmt(data.cashflow_summary.written_off_total)}</span>
                    <span className="text-zinc-600">·{data.cashflow_summary.written_off_count}</span>
                  </div>
                )}
                <ArrowRight className="h-3 w-3 text-zinc-500" />
              </div>
            </div>
          </div>
        </Link>
      )}

      {/* === ROW 2: Cash flow === */}
      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardHeader className="pb-2 flex flex-row items-center justify-between flex-wrap gap-2">
          <CardTitle className="text-base font-semibold text-zinc-50 font-['Manrope'] flex items-center gap-2">
            <Activity className="h-4 w-4 text-emerald-500" />
            Cash flow · last {historyMonths} months ({chartView}){hasForecast && " + 3 ahead"}
          </CardTitle>
          <div className="flex items-center gap-3">
            <div className="inline-flex items-center bg-zinc-950 border border-zinc-800 rounded-md p-0.5" data-testid="cashflow-history-switch">
              {/* Data starts Nov 2025, so 12 months is the whole history;
                  longer windows would only draw empty months. */}
              {[6, 12].map((n) => (
                <Button
                  key={n}
                  variant="ghost"
                  size="sm"
                  onClick={() => setHistoryMonths(n)}
                  title={`Last ${n} months + 3 ahead`}
                  className={cn(
                    "h-7 px-2.5 text-xs",
                    historyMonths === n ? "bg-zinc-700 text-zinc-50 hover:bg-zinc-700" : "text-zinc-400 hover:text-zinc-50"
                  )}
                >
                  {n}m
                </Button>
              ))}
            </div>
            <div className="inline-flex items-center bg-zinc-950 border border-zinc-800 rounded-md p-0.5">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setChartView("plan")}
                className={cn(
                  "h-7 px-3 text-xs",
                  chartView === "plan" ? "bg-blue-600 text-white hover:bg-blue-600" : "text-zinc-400 hover:text-zinc-50"
                )}
              >
                Plan
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setChartView("fact")}
                className={cn(
                  "h-7 px-3 text-xs",
                  chartView === "fact" ? "bg-emerald-600 text-white hover:bg-emerald-600" : "text-zinc-400 hover:text-zinc-50"
                )}
              >
                Fact
              </Button>
            </div>
            <div className="flex items-center gap-3 text-xs">
              <span className="flex items-center gap-1.5 text-emerald-400"><span className="inline-block w-2 h-2 rounded-full bg-emerald-500" /> Income</span>
              <span className="flex items-center gap-1.5 text-red-400"><span className="inline-block w-2 h-2 rounded-full bg-red-500" /> Expenses</span>
              <span className="flex items-center gap-1.5 text-orange-400"><span className="inline-block w-2 h-2 rounded-full bg-orange-500" /> Net</span>
              {hasForecast && (
                <span
                  className="flex items-center gap-1.5 text-zinc-400"
                  data-testid="cashflow-forecast-legend"
                  title={"Dashed = forecast.\nIncome: booked project payments (all probabilities, as Plan) + CRM and support retainers carried over from this month.\n" +
                    (forecastMethod
                      ? `Expenses, calibrated on ${forecastMethod.basis_months.join(", ")}:\n` +
                        `· salary = base payroll ${fmt(forecastMethod.base_payroll)} + bonuses ${forecastMethod.bonus_pct_of_income}% of income\n` +
                        `· artists ${forecastMethod.artist_pct_of_project_income}% + AD ${forecastMethod.ad_fee_pct_of_project_income}% of project income\n` +
                        `· other ${fmt(forecastMethod.other_flat)} and ads ${fmt(forecastMethod.ads_flat)} flat (3-month average)\n`
                      : "") +
                    "Net = income − expenses."}
                >
                  <span className="inline-block w-3 border-t-2 border-dashed border-zinc-400" /> Forecast
                </span>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="h-[26rem]">
            {cashFlow.length === 0 ? (
              <div className="flex items-center justify-center h-full text-sm text-zinc-500">No data for this period</div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={cashFlow} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="incomeFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#10b981" stopOpacity={0.35} />
                      <stop offset="100%" stopColor="#10b981" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="expensesFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#ef4444" stopOpacity={0.25} />
                      <stop offset="100%" stopColor="#ef4444" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke="#27272a" strokeDasharray="3 3" />
                  <XAxis dataKey="month" stroke="#71717a" fontSize={12} />
                  <YAxis
                    stroke="#71717a"
                    fontSize={12}
                    width={56}
                    tickCount={10}
                    domain={[(min) => Math.min(0, Math.floor(min / 25000) * 25000), (max) => Math.ceil(max / 25000) * 25000]}
                    tickFormatter={(v) => `${v < 0 ? "−" : ""}$${Math.abs(Math.round(v / 1000))}k`}
                  />
                  <Tooltip
                    contentStyle={{ background: "#0a0a0a", border: "1px solid #27272a", borderRadius: "6px", fontSize: "12px" }}
                    formatter={(value) => fmt(value)}
                  />
                  <Area
                    type="monotone"
                    dataKey={chartView === "plan" ? "income_plan" : "income_fact"}
                    name="Income"
                    stroke="#10b981"
                    strokeWidth={2}
                    fill="url(#incomeFill)"
                  />
                  <Area
                    type="monotone"
                    dataKey={chartView === "plan" ? "expenses_plan" : "expenses_fact"}
                    name="Expenses"
                    stroke="#ef4444"
                    strokeWidth={2}
                    fill="url(#expensesFill)"
                  />
                  <Line
                    type="monotone"
                    dataKey={chartView === "plan" ? "net_plan" : "net_fact"}
                    name="Net"
                    stroke="#f97316"
                    strokeWidth={2.5}
                    dot={{ fill: "#f97316", r: 4 }}
                  />
                  {hasForecast && (
                    <Line type="monotone" dataKey="income_forecast" name="Income forecast"
                      stroke="#6ee7b7" strokeWidth={2} strokeDasharray="6 4" dot={{ fill: "#6ee7b7", r: 3 }} />
                  )}
                  {hasForecast && (
                    <Line type="monotone" dataKey="expenses_forecast" name="Expenses forecast"
                      stroke="#fca5a5" strokeWidth={2} strokeDasharray="6 4" dot={{ fill: "#fca5a5", r: 3 }} />
                  )}
                  {hasForecast && (
                    <Line type="monotone" dataKey="net_forecast" name="Net forecast"
                      stroke="#fdba74" strokeWidth={2} strokeDasharray="6 4" dot={{ fill: "#fdba74", r: 3 }} />
                  )}
                </ComposedChart>
              </ResponsiveContainer>
            )}
          </div>
        </CardContent>
      </Card>

      {/* === ROW 2.5: Income & Expenses breakdown (stacked bars) === */}
      <div className="flex items-center gap-3 flex-wrap">
        <span className="text-xs text-zinc-500 uppercase font-mono tracking-wide">Breakdown:</span>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setByManager((v) => !v)}
          className={cn(
            "h-7 px-3 text-xs border-zinc-700",
            byManager ? "bg-purple-600 text-white border-purple-600" : "bg-zinc-900 text-zinc-400"
          )}
        >
          <Users className="h-3 w-3 mr-1.5" />
          Income: {byManager ? "By manager" : "By category"}
        </Button>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* INCOME */}
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardHeader className="pb-2 flex flex-row items-center justify-between gap-2">
            <CardTitle className="text-base font-semibold text-zinc-50 font-['Manrope'] flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-emerald-500" />
              Income · {byManager ? "by manager" : "by category"} ({chartView}){hasForecast && !byManager && " + forecast"}
            </CardTitle>
            {!byManager && (
              <div className="flex items-center gap-3 text-xs">
                <span className="flex items-center gap-1.5 text-emerald-400">
                  <span className="inline-block w-2 h-2 rounded-sm bg-emerald-500" /> Sales
                </span>
                <span className="flex items-center gap-1.5 text-orange-400">
                  <span className="inline-block w-2 h-2 rounded-sm bg-orange-500" /> Partner
                </span>
                <span className="flex items-center gap-1.5 text-cyan-400">
                  <span className="inline-block w-2 h-2 rounded-sm bg-cyan-500" /> CRM
                </span>
                <span className="flex items-center gap-1.5 text-teal-400">
                  <span className="inline-block w-2 h-2 rounded-sm bg-teal-400" /> Support
                </span>
              </div>
            )}
          </CardHeader>
          <CardContent>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={byManager ? incomeByManager.rows : incomeFlow}
                  margin={{ top: 22, right: 10, left: 0, bottom: 0 }}
                >
                  <CartesianGrid stroke="#27272a" strokeDasharray="3 3" />
                  <XAxis dataKey="month" stroke="#71717a" fontSize={12} />
                  <YAxis stroke="#71717a" fontSize={12} tickFormatter={(v) => `$${Math.round(v / 1000)}k`} />
                  <Tooltip
                    contentStyle={{ background: "#0a0a0a", border: "1px solid #27272a", borderRadius: "6px", fontSize: "12px" }}
                    formatter={(value, name) => [fmt(value), name]}
                    cursor={{ fill: "#ffffff08" }}
                  />
                  {byManager && <Legend wrapperStyle={{ fontSize: "11px", paddingTop: "8px" }} />}
                  {byManager ? (
                    <>
                      {incomeByManager.managers.map((mgr, i) => (
                        <Bar
                          key={mgr}
                          dataKey={mgr}
                          name={mgr}
                          stackId="income"
                          fill={MANAGER_COLORS[i % MANAGER_COLORS.length]}
                        />
                      ))}
                      <Bar dataKey="CRM (recurring)" name="CRM (recurring)" stackId="income" fill="#71717a" radius={[4, 4, 0, 0]} />
                    </>
                  ) : (
                    <>
                      <Bar
                        dataKey={chartView === "plan" ? "sales_plan" : "sales_fact"}
                        name="Sales"
                        stackId="income"
                        fill="#10b981"
                      />
                      <Bar
                        dataKey={chartView === "plan" ? "partner_plan" : "partner_fact"}
                        name="Partner (owner)"
                        stackId="income"
                        fill="#f97316"
                      />
                      <Bar
                        dataKey={chartView === "plan" ? "crm_plan" : "crm_fact"}
                        name="CRM"
                        stackId="income"
                        fill="#06b6d4"
                      />
                      <Bar
                        dataKey={chartView === "plan" ? "support_plan" : "support_fact"}
                        name="Support"
                        stackId="income"
                        fill="#2dd4bf"
                      />
                      <Bar dataKey="sales_fc" name="Sales (expected)" stackId="income" fill="#10b981" fillOpacity={0.3} />
                      <Bar dataKey="partner_fc" name="Partner (expected)" stackId="income" fill="#f97316" fillOpacity={0.3} />
                      <Bar dataKey="crm_fc" name="CRM (expected)" stackId="income" fill="#06b6d4" fillOpacity={0.3} />
                      <Bar dataKey="support_fc" name="Support (expected)" stackId="income" fill="#2dd4bf" fillOpacity={0.3} />
                    </>
                  )}
                  <Bar
                    dataKey={() => 0}
                    stackId="income"
                    fill="transparent"
                    tooltipType="none"
                    legendType="none"
                    isAnimationActive={false}
                    label={
                      byManager
                        ? stackTotalLabel(incomeByManager.rows, (r) =>
                            Object.entries(r).reduce((acc, [key, v]) => acc + (key !== "month" && typeof v === "number" ? v : 0), 0)
                          )
                        : stackTotalLabel(incomeFlow, (r) => r.label_total ?? r[chartView === "plan" ? "income_plan" : "income_fact"])
                    }
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* EXPENSES */}
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardHeader className="pb-2 flex flex-row items-center justify-between gap-2">
            <CardTitle className="text-base font-semibold text-zinc-50 font-['Manrope'] flex items-center gap-2">
              <TrendingDown className="h-4 w-4 text-red-500" />
              Expenses · by category ({chartView}){hasForecast && " + forecast"}
            </CardTitle>
            <div className="flex items-center gap-3 text-xs">
              <span className="flex items-center gap-1.5 text-orange-400"><span className="inline-block w-2 h-2 rounded-sm bg-orange-500" /> Salary</span>
              <span className="flex items-center gap-1.5 text-violet-400"><span className="inline-block w-2 h-2 rounded-sm bg-violet-500" /> Artists</span>
              <span className="flex items-center gap-1.5 text-pink-400"><span className="inline-block w-2 h-2 rounded-sm bg-pink-500" /> AD</span>
              <span className="flex items-center gap-1.5 text-zinc-400"><span className="inline-block w-2 h-2 rounded-sm bg-zinc-500" /> Other</span>
              <span className="flex items-center gap-1.5 text-sky-400"><span className="inline-block w-2 h-2 rounded-sm bg-sky-500" /> Ads</span>
              {chartView === "plan" && (
                <span className="flex items-center gap-1.5 text-red-400"><span className="inline-block w-2 h-2 rounded-sm bg-red-600" /> Bad debt</span>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={expenseFlow} margin={{ top: 22, right: 10, left: 0, bottom: 0 }}>
                  <CartesianGrid stroke="#27272a" strokeDasharray="3 3" />
                  <XAxis dataKey="month" stroke="#71717a" fontSize={12} />
                  <YAxis stroke="#71717a" fontSize={12} tickFormatter={(v) => `$${Math.round(v / 1000)}k`} />
                  <Tooltip
                    contentStyle={{ background: "#0a0a0a", border: "1px solid #27272a", borderRadius: "6px", fontSize: "12px" }}
                    formatter={(value, name) => [fmt(value), name]}
                    cursor={{ fill: "#ffffff08" }}
                  />
                  <Bar dataKey={chartView === "plan" ? "salary_plan" : "salary_fact"} name="Salary" stackId="expenses" fill="#f97316" />
                  <Bar dataKey={chartView === "plan" ? "artist_plan" : "artist_fact"} name="Artists" stackId="expenses" fill="#a78bfa" />
                  <Bar dataKey={chartView === "plan" ? "ad_fee_plan" : "ad_fee_fact"} name="AD fees" stackId="expenses" fill="#ec4899" />
                  <Bar dataKey={chartView === "plan" ? "other_plan" : "other_fact"} name="Other" stackId="expenses" fill="#71717a" />
                  <Bar dataKey={chartView === "plan" ? "ads_plan" : "ads_fact"} name="Ad spend" stackId="expenses" fill="#0ea5e9" />
                  {chartView === "plan" && (
                    <Bar dataKey="bad_debt_plan" name="Bad debt (90+ unpaid)" stackId="expenses" fill="#dc2626" />
                  )}
                  <Bar dataKey="salary_fc" name="Salary (expected)" stackId="expenses" fill="#f97316" fillOpacity={0.35} />
                  <Bar dataKey="artist_fc" name="Artists (expected)" stackId="expenses" fill="#a78bfa" fillOpacity={0.35} />
                  <Bar dataKey="ad_fee_fc" name="AD fees (expected)" stackId="expenses" fill="#ec4899" fillOpacity={0.35} />
                  <Bar dataKey="other_fc" name="Other (expected)" stackId="expenses" fill="#71717a" fillOpacity={0.35} />
                  <Bar dataKey="ads_fc" name="Ad spend (expected)" stackId="expenses" fill="#0ea5e9" fillOpacity={0.35} />
                  <Bar
                    dataKey={() => 0}
                    stackId="expenses"
                    fill="transparent"
                    tooltipType="none"
                    legendType="none"
                    isAnimationActive={false}
                    label={stackTotalLabel(expenseFlow, (r) => r.label_total ?? r[chartView === "plan" ? "expenses_plan" : "expenses_fact"])}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* === ROW 3: Pipeline + AR === */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardHeader className="pb-2 flex flex-row items-center justify-between">
            <CardTitle className="text-base font-semibold text-zinc-50 font-['Manrope'] flex items-center gap-2">
              <Target className="h-4 w-4 text-blue-400" />
              Pipeline & forecast
            </CardTitle>
            <Button variant="ghost" size="sm" onClick={() => navigate("/sales-leads")} className="text-orange-500 hover:text-orange-400 h-8 px-2">Leads &amp; funnel <ArrowRight className="ml-1 h-3 w-3" /></Button>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-md border border-zinc-800 bg-zinc-950/40 p-3">
                <p className="text-xs text-zinc-500 uppercase font-mono">Total pipeline</p>
                <p className="text-xl font-bold text-zinc-50 mt-1">{fmt(pipelineTotalValue)}</p>
                <p className="text-xs text-zinc-500 mt-0.5">{pipelineTotalDeals} active deals</p>
              </div>
              <div className="rounded-md border border-blue-500/30 bg-blue-500/5 p-3">
                <p className="text-xs text-blue-400 uppercase font-mono">Weighted forecast</p>
                <p className="text-xl font-bold text-zinc-50 mt-1">{fmt(pipelineTotalWeighted)}</p>
                <p className="text-xs text-blue-300/60 mt-0.5">by stage probability</p>
              </div>
            </div>

            {pipeline.length === 0 ? (
              <p className="text-sm text-zinc-500 text-center py-3">No active deals in pipeline</p>
            ) : (
              <div className="space-y-2">
                {pipeline.map((p, i) => {
                  const widthPct = pipelineTotalValue > 0 ? (p.value / pipelineTotalValue) * 100 : 0;
                  return (
                    <div key={p.stage_id || p.stage}>
                      <div className="flex items-center justify-between text-xs mb-1">
                        <span className="text-zinc-300 font-medium">{p.stage}</span>
                        <span className="text-zinc-500 font-mono">{p.deals} · {fmt(p.value)} → {fmt(p.weighted)}</span>
                      </div>
                      <div className="h-1.5 bg-zinc-800 rounded-full overflow-hidden">
                        <div className={cn("h-full rounded-full", stageColor(i))} style={{ width: `${widthPct}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            <div className="pt-3 border-t border-zinc-800">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-zinc-400 uppercase tracking-wide font-mono">Closing this month</span>
                <span className="text-xs font-mono text-zinc-500">{fmt(closingTotal)} → {fmt(closingWeighted)}</span>
              </div>
              {closing.length === 0 ? (
                <p className="text-xs text-zinc-500 text-center py-2">No deals closing this month</p>
              ) : (
                <div className="space-y-1.5">
                  {closing.map((d) => (
                    <div key={d.deal_id || d.name} className="flex items-center justify-between text-xs py-1">
                      <div className="flex items-center gap-2 min-w-0">
                        {d.risk ? <Flame className="h-3 w-3 text-red-400 shrink-0" /> : <span className="inline-block w-3 h-3 rounded-full bg-emerald-500/20 border border-emerald-500/40 shrink-0" />}
                        <span className="text-zinc-300 truncate">{d.name}</span>
                        <span className="text-zinc-600 hidden sm:inline">· {d.owner}</span>
                      </div>
                      <div className="flex items-center gap-3 shrink-0">
                        <span className="text-zinc-500 font-mono">{d.prob}%</span>
                        <span className="text-zinc-50 font-mono">{fmt(d.value)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardHeader className="pb-2 flex flex-row items-center justify-between">
            <CardTitle className="text-base font-semibold text-zinc-50 font-['Manrope'] flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-orange-500" />
              Accounts receivable aging
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-baseline justify-between">
              <div>
                <p className="text-xs text-zinc-500 uppercase font-mono">Total outstanding</p>
                <p className="text-2xl font-bold text-zinc-50 mt-1">{fmt(arTotal)}</p>
                {arWrittenOff > 0 && (
                  <p className="text-xs text-red-400 mt-0.5">+ {fmt(arWrittenOff)} written off</p>
                )}
              </div>
              <div className="text-right">
                <p className="text-xs text-zinc-500 uppercase font-mono">Overdue</p>
                <p className="text-lg font-bold text-orange-400 mt-1">{arOverdueShare.toFixed(0)}%</p>
              </div>
            </div>

            {arAging.length === 0 ? (
              <p className="text-sm text-zinc-500 text-center py-3">No outstanding invoices</p>
            ) : (
              <div className="space-y-3">
                {arAging.map((b, i) => {
                  const widthPct = arScale > 0 ? (b.amount / arScale) * 100 : 0;
                  return (
                    <div key={b.bucket} className={cn(b.written_off && "pt-3 border-t border-dashed border-zinc-800")}>
                      <div className="flex items-center justify-between text-xs mb-1.5">
                        {b.written_off ? (
                          <span className="text-red-400 font-medium" data-testid="ar-written-off-label">{b.bucket} · written off, counted as a loss</span>
                        ) : (
                          <span className="text-zinc-300 font-medium">{b.bucket}</span>
                        )}
                        <span className={cn("font-mono", b.written_off ? "text-red-400" : "text-zinc-500")}>
                          {b.count} invoices · {b.written_off && b.amount ? "−" : ""}{fmt(b.amount)}
                        </span>
                      </div>
                      <div className="h-2 bg-zinc-800 rounded-full overflow-hidden">
                        <div className={cn("h-full rounded-full", arBucketColor(i))} style={{ width: `${widthPct}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            <div className="pt-3 border-t border-zinc-800 grid grid-cols-2 gap-3">
              <div className="rounded-md bg-zinc-950/50 border border-zinc-800 p-3">
                <p className="text-xs text-zinc-500">DSO (avg days to pay)</p>
                <p className="text-lg font-bold text-zinc-50 mt-1">{data?.ar_dso_days || 0} days</p>
              </div>
              <div className="rounded-md bg-zinc-950/50 border border-zinc-800 p-3">
                <p className="text-xs text-zinc-500">Worst offender</p>
                {worstOffender ? (
                  <>
                    <p className="text-sm font-bold text-zinc-50 mt-1 truncate">{worstOffender.client_name}</p>
                    <p className="text-xs text-red-400 mt-0.5">{fmtK(worstOffender.amount)} · {worstOffender.days}d</p>
                  </>
                ) : (
                  <p className="text-sm text-zinc-500 mt-1">—</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* === ROW 4: Top clients + Project profitability === */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="bg-zinc-900/50 border-zinc-800 lg:col-span-1">
          <CardHeader className="pb-2 flex flex-row items-center justify-between">
            <CardTitle className="text-base font-semibold text-zinc-50 font-['Manrope'] flex items-center gap-2">
              <Trophy className="h-4 w-4 text-yellow-500" />
              Top clients · YTD
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            {topClients.length === 0 ? (
              <p className="text-sm text-zinc-500 text-center py-6">No client revenue YTD</p>
            ) : (
              <div className="space-y-2.5">
                {topClients.map((c, i) => (
                  <div key={c.client_id || c.name} className="space-y-1">
                    <div className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="text-zinc-600 font-mono text-xs w-4">{i + 1}</span>
                        <span className="text-zinc-50 font-medium truncate">{c.name}</span>
                      </div>
                      <span className="text-zinc-50 font-mono text-sm shrink-0">{fmtK(c.revenue_ytd)}</span>
                    </div>
                    <div className="flex items-center gap-2 pl-6">
                      <div className="flex-1 h-1 bg-zinc-800 rounded-full overflow-hidden">
                        <div className="h-full bg-gradient-to-r from-orange-500 to-yellow-500 rounded-full" style={{ width: `${Math.min(100, (c.share_pct || 0) * 4)}%` }} />
                      </div>
                      <span className="text-xs text-zinc-500 font-mono shrink-0">{c.projects} proj</span>
                      <span className={cn("text-xs font-mono shrink-0 w-12 text-right", c.growth_pct > 0 ? "text-emerald-400" : c.growth_pct < 0 ? "text-red-400" : "text-zinc-500")}>
                        {c.growth_pct > 0 ? "+" : ""}{c.growth_pct}%
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {topClients.length > 0 && (
              <div className="mt-4 pt-3 border-t border-zinc-800">
                <p className="text-xs text-zinc-500">
                  Top-3 = <span className="text-orange-400 font-mono">{top3Concentration}%</span> of revenue · concentration risk
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="bg-zinc-900/50 border-zinc-800 lg:col-span-2">
          <CardHeader className="pb-2 flex flex-row items-center justify-between">
            <CardTitle className="text-base font-semibold text-zinc-50 font-['Manrope'] flex items-center gap-2">
              <Building2 className="h-4 w-4 text-emerald-500" />
              Project profitability · closed & paid in {periodLabel}
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            {profitability.length === 0 ? (
              <p className="text-sm text-zinc-500 text-center py-6">No fully paid completed projects in this period</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="border-zinc-800 hover:bg-transparent">
                    <TableHead className="text-zinc-500 text-xs font-medium">Project</TableHead>
                    <TableHead className="text-zinc-500 text-xs font-medium text-right">Revenue</TableHead>
                    <TableHead className="text-zinc-500 text-xs font-medium text-right">Costs</TableHead>
                    <TableHead className="text-zinc-500 text-xs font-medium text-right">Margin</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {profitability.map((p) => (
                    <TableRow
                      key={p.project_id || p.name}
                      className={cn("border-zinc-800 hover:bg-zinc-800/30", p.project_id && "cursor-pointer")}
                      onClick={() => p.project_id && navigate(`/unified-projects/${p.project_id}`)}
                    >
                      <TableCell className="py-2.5">
                        <div className="flex items-center gap-2">
                          {p.warn && <AlertTriangle className="h-3 w-3 text-orange-400 shrink-0" />}
                          <span className="text-zinc-50 text-sm font-medium hover:underline">{p.name}</span>
                        </div>
                      </TableCell>
                      <TableCell className="py-2.5 text-right text-sm text-zinc-50 font-mono">{fmt(p.revenue)}</TableCell>
                      <TableCell className="py-2.5 text-right text-sm text-zinc-400 font-mono">{fmt(p.costs)}</TableCell>
                      <TableCell className="py-2.5 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <span className={cn("text-sm font-mono font-bold", p.margin_pct >= 30 ? "text-emerald-400" : p.margin_pct >= 10 ? "text-yellow-400" : "text-red-400")}>
                            {p.margin_pct >= 0 ? "+" : ""}{p.margin_pct.toFixed(1)}%
                          </span>
                          <div className="w-12 h-1.5 bg-zinc-800 rounded-full overflow-hidden">
                            <div className={cn("h-full rounded-full", p.margin_pct >= 30 ? "bg-emerald-500" : p.margin_pct >= 10 ? "bg-yellow-500" : "bg-red-500")} style={{ width: `${Math.max(0, Math.min(100, Math.abs(p.margin_pct) * 2))}%` }} />
                          </div>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      {/* === ROW 5: Team capacity (Worksection-driven, shared with /team-utilization) === */}
      <TeamUtilizationSection year={year} month={month} />

      {/* footer */}
      <div className="text-xs text-zinc-600 text-center pt-2 pb-2">
        All amounts shown in USD · converted from EUR/UAH at latest PrivatBank rates · Period: {periodLabel}
      </div>
    </div>
  );
};

export default DashboardV2Page;
